import React, { useContext, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Modal,
  Radio,
  RadioChangeEvent,
  Typography,
  message,
} from "antd";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  CitiesOpts,
  CountriesOpts,
  StatesOpts,
} from "../../../constants/countries.constants";
import { designationsOptions } from "../../../constants/designations.constants";
import { employmentOptions } from "../../../constants/employment.constants";
import { getCourseById } from "../../../services/course.service";
import { CBInput, CBSelect } from "../../shared";
import { CBHandMessage } from "../../shared/CBHandMessage/CBHandMessage";
import RegistrationCostTable from "../shared/registration-cost-table";
import { UserContext } from "../../../context/user.context";
import { Costs } from "../../../interfaces/course-cost.interface";
import { CourseRegistrationDTO } from "../../../interfaces/course-registration.interface";
import {
  courseRegistrationScheme,
  courseRegistrationValues,
} from "../shared/utils";
import {
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import CustomCard from "../../shared/CustomCard/CustomCard";
import GSTLicenses from "../../../interfaces/gst-licenses";
import dayjs from "dayjs";
interface Props {
  registrationSelected: string[];
  changeStep: (obj: CourseRegistrationDTO) => void;
}
const CourseRegistrationStep1 = (props: Props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [modal, contextHolderModal] = Modal.useModal();
  const [messageApi, contextHolderMessage] = message.useMessage();
  const [country, setCountry] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [courseType, setCourseType] = useState<Costs>();
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [licenseInfo, setLicenseInfo] = useState<GSTLicenses | undefined>();
  const [licensePurchase, setLicensePurchase] = useState<boolean>(false);
  const [waitListCondition, setWaitListCondition] = useState<boolean>(false);
  const [fullCourseCondition, setFullCourseCondition] =
    useState<boolean>(false);
  const countries = CountriesOpts();
  const states = StatesOpts(country);
  const cities = CitiesOpts(country, state);
  const userContext = useContext(UserContext);
  const color = "yellow";
  const user = userContext.user;
  const leyend =
    "To finish registering for this course, please complete the form below";
  const { Text } = Typography;
  const selectOptions = [
    { value: "1", label: "Yes" },
    { value: "0", label: "No" },
  ];

  const { control, watch, handleSubmit, setValue } = useForm({
    mode: "onBlur",
    resolver: yupResolver(courseRegistrationScheme),
    values: courseRegistrationValues(user!),
  });

  const onChangeTitle = (e: RadioChangeEvent) =>
    setValue("title", e.target.value, { shouldDirty: true });
  const changeCountry = (val: string) => {
    const element = countries.find((item) => {
      return item.value === val;
    });
    setCountry(element?.code ?? "");
  };

  const changeState = (val: string) => {
    const element = states.find((item) => {
      return item.value === val;
    });
    setState(element?.code ?? "");
  };

  const { data } = useQuery({
    queryKey: [`course-${id}`],
    queryFn: () => getCourseById(id),
    select: (data) => ({
      ...data,
      costs: data?.costs?.map((cost, index) => {
        return { ...cost, key: `cost-${index}` };
      }),
    }),
  });

  const changeCourseCost = (keys: React.Key[], obj: Costs[]) => {
    setCourseType(obj[0]);
    setErrorMsg("");
  };

  const changeLicense = (licensePurchase: string) => {
    setValue("licensePurchase", licensePurchase === "1" ? true : false, {
      shouldDirty: true,
    });
    setLicensePurchase(licensePurchase === "1" ? true : false);
  };

  const changeWaitList = (key: boolean, waitList: string) => {
    setValue("waitList", key, { shouldDirty: true });
    setWaitListCondition(key ? (waitList === "1" ? false : true) : false);
  };

  const handleFullCourse = (courseFull: boolean) => {
    setFullCourseCondition(courseFull);
  };

  const submitHandler = (obj: CourseRegistrationDTO) => {
    if (courseType) {
      const gst = [
        data?.gstLicenses!.find((ls) => ls.title === "GST" && ls.enable === 1),
      ];
      const license = [
        data?.gstLicenses!.find(
          (ls) => ls.title === "License" && ls.enable === 1,
        ),
      ];
      const beforeDueDateDiscount =
        dayjs(new Date()).format("MM/DD/YYYY") <=
        dayjs(courseType.earlyDueDate).format("MM/DD/YYYY")
          ? Number(courseType.discountBeforeDueDate)
          : 0;
      const memberDiscount = Number(
        obj.member === "1" ? courseType.discountMember : 0,
      );
      const licenseCost = Number(
        licensePurchase ? (license[0]?.value ?? 0) : 0,
      );
      const subTotal =
        Number(courseType.baseCost) +
        licenseCost -
        beforeDueDateDiscount -
        memberDiscount;

      const course = {
        id: Number(id),
        key: courseType?.key,
        costId: courseType?.id,
        code: data?.code,
        name: data?.name,
        registrationType: courseType?.registrationType,
        paymentMethods: courseType?.paymentMethods,
        price: courseType?.baseCost,
        license: license,
        memberDiscount: memberDiscount.toFixed(2),
        subtotalPrice: subTotal.toFixed(2),
        gst: gst,
        earlyDueDate: courseType.earlyDueDate,
        discountBeforeDueDate: beforeDueDateDiscount.toFixed(2),
      };
      setErrorMsg("");
      setValue("course", course, { shouldDirty: true });
      props.changeStep(obj);
    } else {
      setErrorMsg("You need to select a registration type to continue!");
      notificationMessage(
        "You need to select a registration type to continue!",
      );
    }
  };

  const notificationMessage = (msg: string) => {
    messageApi.open({
      type: "error",
      content: msg,
      duration: 4,
      style: { marginTop: "6vh" },
    });
  };

  const confirm = () => {
    modal.confirm({
      title: `Confirm cancel registration`,
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to go back to the course details?`,
      okText: "Ok",
      cancelText: "Cancel",
      onOk: () => {
        navigate(`/SEABC/courses/${id}`);
      },
    });
  };

  const endProcess = () => {
    confirm();
  };

  useEffect(() => {
    const license = [
      data?.gstLicenses!.find(
        (ls) => ls.title === "License" && ls.enable === 1,
      ),
    ];
    setLicenseInfo(license[0]);
  }, [data]);

  return (
    <div className="pt-5">
      <CBHandMessage color={color}>{leyend}</CBHandMessage>
      <form
        className="flex flex-col  gap-10 text-center pt-10"
        onSubmit={handleSubmit(submitHandler)}
      >
        <CustomCard title={"Select your course option:"}>
          <div style={{ margin: "0 auto" }} className="p-5 max-w-4xl">
            <RegistrationCostTable
              registrationSelected={props.registrationSelected ?? ""}
              onChange={changeCourseCost}
              license={licenseInfo}
              handleLicense={changeLicense}
              handleWaitList={changeWaitList}
              handleFullCourse={handleFullCourse}
              error={errorMsg}
            />
          </div>
        </CustomCard>
        <CustomCard title="Add your contact information:">
          <div className="flex gap-10 p-5">
            <CBInput
              className="flex-1"
              control={control}
              type="email"
              label="Email"
              name="email"
              required
              disabled
            />
            <CBInput
              className="flex-1"
              control={control}
              type="email"
              label="Contact Email"
              name="contactEmail"
              required
            />
            <CBInput
              className="flex-1"
              control={control}
              type="phone"
              label="Phone"
              name="phone"
              required
            />
          </div>
        </CustomCard>
        <CustomCard title="Add your personal information:">
          <div className="p-5">
            <div className="flex gap-5 pt-5 pb-2">
              <Text>Title:</Text>
              <Radio.Group
                onChange={onChangeTitle}
                value={watch("title") ? watch("title") : "none"}
                className="flex gap-5"
              >
                <Radio value={"mr"}>Mr.</Radio>
                <Radio value={"ms"}>Ms.</Radio>
                <Radio value={"dr"}>Dr.</Radio>
                {watch("title") === "none" && (
                  <Radio value={"none"}>None</Radio>
                )}
              </Radio.Group>
            </div>
            <div className="flex gap-10">
              <CBSelect
                className="flex-none w-32"
                control={control}
                label="Designation:"
                name="designation"
                required
                defaultValue={"No"}
                options={designationsOptions}
              />
              <CBInput
                className="flex-1"
                control={control}
                type="text"
                label="Last Name:"
                name="lastName"
                required
              />
              <CBInput
                className="flex-1"
                control={control}
                type="text"
                label="First Name: "
                name="firstName"
                required
              />
              <CBInput
                control={control}
                label="Year obtained bachelor’s degree:"
                name="degreeYear"
                required
              />
            </div>
            <div className="flex gap-10">
              <CBSelect
                className="flex-none w-32"
                control={control}
                label="SEABC Member:"
                name="member"
                required
                defaultValue={"No"}
                options={selectOptions}
              />
              {watch("member") === "1" && (
                <CBInput
                  control={control}
                  label="Membership number:"
                  name="membership"
                  required
                />
              )}

              <CBSelect
                className="flex-none w-40"
                control={control}
                label="Employment status:"
                name="employmentStatus"
                required
                defaultValue={"Employed"}
                options={employmentOptions}
              />
              {watch("employmentStatus") === "2" && (
                <CBInput
                  type="text"
                  control={control}
                  label="Name of firm:"
                  name="employer"
                  required
                />
              )}
            </div>
            <div className="flex gap-10">
              <CBInput
                className="flex-none w-32"
                control={control}
                type="text"
                label="Postal Code:"
                name="zipCode"
                required
              />
              <CBSelect
                className="flex-1"
                searchable={true}
                control={control}
                label="Country:"
                name="country"
                required
                defaultValue={"No"}
                options={countries}
                onChange={changeCountry}
              />
              {states.length ? (
                <CBSelect
                  searchable={true}
                  className="flex-1"
                  control={control}
                  label="Province:"
                  name="province"
                  required
                  defaultValue={"No"}
                  options={states}
                  onChange={changeState}
                  disabled={country ? false : true}
                />
              ) : (
                <CBInput
                  className="flex-1"
                  control={control}
                  type="text"
                  label="Province"
                  name="province"
                />
              )}
              {cities.length ? (
                <CBSelect
                  searchable={true}
                  control={control}
                  label="City:"
                  name="city"
                  required
                  className="flex-1"
                  defaultValue={"No"}
                  options={cities}
                  disabled={country && state ? false : true}
                />
              ) : (
                <CBInput
                  className="flex-1"
                  control={control}
                  type="text"
                  label="City"
                  name="city"
                  required
                />
              )}
            </div>
            <div className="flex gap-10">
              <CBInput
                className="flex-none w-32"
                control={control}
                type="text"
                label="Apt./Suite:"
                name="aptSuite"
              />
              <CBInput
                className="flex-1"
                control={control}
                type="text"
                label="Address"
                name="address"
                required
              />
            </div>
          </div>
        </CustomCard>
        <div className="flex justify-center gap-8">
          <Button
            disabled={waitListCondition || fullCourseCondition}
            style={{ height: "2.3rem" }}
            htmlType="submit"
            type="primary"
            icon={<CheckOutlined />}
          >
            Continue and review my order
          </Button>
          <Button
            style={{ height: "2.3rem" }}
            type="primary"
            danger
            icon={<CloseOutlined />}
            onClick={endProcess}
          >
            Cancel
          </Button>
        </div>
      </form>
      {contextHolderMessage}
      {contextHolderModal}
    </div>
  );
};

export default CourseRegistrationStep1;
